import { useCallback, useEffect, useState } from "react";
import type { Oekt } from "../state/session";
import { Topp } from "./oppsett/deler";
import * as profil from "../state/profil";
import {
  hentFagfunksjoner, hentMin, selvvalgte, velg, ROLLENAVN,
  type Fagfunksjon, type MinFagfunksjon,
} from "../state/fagfunksjoner";

const dato = (iso: string): string =>
  new Date(iso).toLocaleDateString("no-NO", { day: "2-digit", month: "short" });

/**
 * Profilen.
 *
 * ========  DET SOM ER DITT  ========
 *
 * Fagfunksjonen din, arbeidet du har lagt igjen, og opplysningene
 * produksjonen har om deg. Tre ting som svarer på det samme spørsmålet:
 * hva har denne produksjonen av meg?
 *
 * Teamet står IKKE her. Det hører i menyen, fordi det kan endre seg fra
 * dag til dag — turnus og sykdom flytter folk — og da skal det være ett
 * trykk unna mens man jobber. Fagfunksjonen setter man én gang.
 *
 * ========  DEN ER DIN, UANSETT ROLLE  ========
 *
 * Ingen rollegating her. Alle har en profil, og alle har de samme
 * rettighetene etter kapittel III i forordningen.
 */
export function ProfilScreen({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  return (
    <div className="flate flate--oppsett">
      <div className="flate__innhold">
        <Topp
          tittel="Profil"
          under={`${oekt.member.displayName} · ${oekt.production.name}`}
        />
        <div className="oppsett__rutenett">
          <FagfunksjonKort oekt={oekt} />
          <ArbeidKort oekt={oekt} />
          <OpplysningerKort oekt={oekt} />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */

/**
 * Fagfunksjonen. Flyttet hit fra menyen 27.10.2026.
 *
 * Den hører hjemme her og ikke i menyen: den sier hva du ER, ikke hva du
 * ser på. Og man setter den én gang, ikke hver dag.
 *
 * Bare det man kan velge selv står i lista. Prodleder, produsent, redaktør
 * og A-foto kommer med invitasjon — en valgmulighet som ikke virker er
 * verre enn ingen.
 */
function FagfunksjonKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [liste, setListe] = useState<readonly Fagfunksjon[]>([]);
  const [min, setMin] = useState<MinFagfunksjon | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => setMin(await hentMin(oekt)))();
  }, [oekt]);

  useEffect(() => {
    void (async () => setListe(selvvalgte(await hentFagfunksjoner())))();
    les();
  }, [les]);

  const grupper = [...new Set(liste.map((f) => f.gruppe))];

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Fagfunksjon</span>
        <span className="loggkort__lag">
          {ROLLENAVN[min?.rolle ?? oekt.member.role]}
        </span>
      </div>
      <div className="oppsett__felt">
        <select
          className="meny__velger"
          value={min?.craftId ?? ""}
          onChange={(e) => {
            const id = e.target.value;
            if (id === "") return;
            void (async () => {
              const svar = await velg(oekt, id);
              setFeil(svar.ok ? null : svar.feil ?? "Valget ble ikke lagret.");
              les();
            })();
          }}
          aria-label="Fagfunksjon"
        >
          <option value="">Velg fagfunksjon …</option>
          {grupper.map((g) => (
            <optgroup key={g} label={g}>
              {liste.filter((f) => f.gruppe === g).map((f) => (
                <option key={f.id} value={f.id}>{f.navn}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <p className="merknad oppsett__merknad">
        Avgjør hva du kan skrive — alle leser alt i produksjonen uansett.
        Produksjonsleder, produsent, redaktør og A-foto settes av prodleder
        under oppsettet.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

/** Det du har lagt igjen i produksjonen. */
function ArbeidKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [a, setA] = useState<profil.MittArbeid>(
    { dokumenter: [], rapporter: [], timer: [] });

  useEffect(() => {
    void (async () => setA(await profil.hentMittArbeid(oekt)))();
  }, [oekt]);

  const tomt = a.dokumenter.length === 0 && a.rapporter.length === 0
    && a.timer.length === 0;

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Mitt arbeid</span>
      </div>

      {tomt ? <p className="tomtekst">Ingenting ført på deg ennå.</p> : null}

      {a.dokumenter.length === 0 ? null : (
        <>
          <div className="deltakere__skille">Dokumenter du har lastet opp</div>
          {a.dokumenter.map((d) => (
            <div key={d.id} className="deltakere__rad">
              <span className="deltakere__navn">{d.tittel}</span>
              <span className="loggkort__lag">{dato(d.dato)}</span>
            </div>
          ))}
        </>
      )}

      {a.rapporter.length === 0 ? null : (
        <>
          <div className="deltakere__skille">Rapporter du har skrevet</div>
          {a.rapporter.map((r) => (
            <div key={r.id} className="deltakere__rad">
              <span className="deltakere__navn">Dag {r.dag}</span>
              <span className="loggkort__lag">{dato(r.dato)}</span>
            </div>
          ))}
        </>
      )}

      <div className="deltakere__skille">Timer</div>
      {a.timer.length === 0 ? (
        <p className="merknad oppsett__merknad">
          Ingen timer ført. Timeføringen kommer i en senere fase — og timer
          hentes aldri fra loggen, de stemples inn og ut av deg selv.
        </p>
      ) : a.timer.map((t) => (
        <div key={t.id} className="deltakere__rad">
          <span className="deltakere__navn">{dato(t.start)}</span>
          <span className="loggkort__lag">
            {t.slutt === null ? "går nå" : "ført"}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Opplysningene, og rettighetene som følger med dem.
 *
 * ========  SLETTEKNAPPEN FINNES IKKE, OG DET SKAL STÅ  ========
 *
 * `production_member.user_id` er `on delete restrict` med vilje: loggen må
 * kunne vise hvem som skrev en post, også etter at personen er ute. En
 * knapp som sa «slett meg» og ikke gjorde det ville vært verre enn ingen
 * knapp.
 *
 * Avveiningen mellom art. 17 og integriteten i loggen tilhører den
 * behandlingsansvarlige — produksjonsselskapet. Flata gir forespørselen et
 * sted, en dato og en eier, og sier rett ut hva som skjer videre.
 */
function OpplysningerKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [notat, setNotat] = useState("");
  const [sendt, setSendt] = useState<readonly profil.Sletteforespoersel[]>([]);
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => setSendt(await profil.hentSletteforespoersler()))();
  }, []);
  useEffect(les, [les]);

  const lastNed = useCallback((): void => {
    void (async () => {
      const fil = profil.somFil(oekt, await profil.hentMittArbeid(oekt));
      const url = URL.createObjectURL(fil);
      const a = document.createElement("a");
      a.href = url;
      a.download = `infoflyt-${oekt.member.initials.toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    })();
  }, [oekt]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Dine opplysninger</span>
      </div>

      <span className="meny__lesefelt" style={{ padding: "var(--s-md)" }}>
        {oekt.member.displayName} · {oekt.member.initials}
        <br />
        {ROLLENAVN[oekt.member.role]} i {oekt.production.name}
      </span>

      <p className="merknad oppsett__merknad">
        Innloggingen samler inn e-postadressen din, og ikke noe mer. Navn og
        forkortelse ligger på medlemskapet, fordi loggen må kunne vise hvem
        som skrev en post.
      </p>

      <div className="deltakere__ny">
        <button type="button" className="synk__knapp" onClick={lastNed}>
          Last ned mine data
        </button>
      </div>

      <div className="deltakere__skille">Be om sletting</div>

      {sendt.map((s) => (
        <div key={s.id} className="deltakere__rad">
          <span className="deltakere__navn">{dato(s.dato)}</span>
          <span className="loggkort__lag">
            {s.status === "open" ? "til behandling"
              : s.status === "done" ? "utført" : "avslått"}
          </span>
        </div>
      ))}

      <form
        className="deltakere__ny"
        onSubmit={(e) => {
          e.preventDefault();
          void (async () => {
            const svar = await profil.beOmSletting(oekt.member.id, notat);
            if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke sendt."); return; }
            setFeil(null); setNotat(""); les();
          })();
        }}
      >
        <input
          className="deltakere__felt" value={notat}
          onChange={(e) => setNotat(e.target.value)}
          placeholder="Hva vil du ha slettet?" aria-label="Hva vil du ha slettet"
        />
        <button type="submit" className="synk__knapp" disabled={notat.trim() === ""}>
          Send
        </button>
      </form>

      {/* HVA SOM FAKTISK SKJER. En flate som later som den sletter er verre
          enn en som sier hvorfor den ikke kan. */}
      <p className="merknad oppsett__merknad">
        Dette sletter ikke med én gang, og det er med vilje: loggen må kunne
        vise hvem som skrev en post, også lenge etter at du er ute av
        produksjonen. Forespørselen går til produksjonsselskapet, som er
        behandlingsansvarlig og avgjør hva som kan fjernes. Du får svaret
        her.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}
